import { MIN_MARGIN_AMOUNT } from '@kwenta/sdk/constants'
import { floorNumber } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import { type ChangeEvent, type FC, memo, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { ConditionOrderTableItem } from 'types/futures'

import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import SelectorButtons from 'components/SelectorButtons'
import { Body } from 'components/Text'
import { FlexDivRow } from 'components/layout/flex'
import { selectEditCOModalInputs } from 'state/futures/common/selectors'
import { editSmartMarginConditionalOrderMargin } from 'state/futures/isolatedMargin/actions'
import { selectIsolatedAvailableMargin } from 'state/futures/isolatedMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

const PERCENT_OPTIONS = ['10%', '25%', '50%', '100%']

type MarginInputProps = {
	order: ConditionOrderTableItem
}

const EditOrderMarginInput: FC<MarginInputProps> = memo(({ order }) => {
	const dispatch = useAppDispatch()

	const idleMargin = useAppSelector(selectIsolatedAvailableMargin)
	const { margin } = useAppSelector(selectEditCOModalInputs)
	const initialValue = useMemo(() => order.marginDelta.toString(2), [order.marginDelta])
	const [isUserInput, setIsUserInput] = useState(false)

	const maxMargin = wei(idleMargin).add(order.marginDelta)

	const onSizeChange = useCallback(
		(value: string) => {
			setIsUserInput(true)
			dispatch(editSmartMarginConditionalOrderMargin(order, value))
		},
		[dispatch, order]
	)

	const onChangeValue = useCallback(
		(_: ChangeEvent<HTMLInputElement>, v: string) => {
			onSizeChange(v)
		},
		[onSizeChange]
	)

	// biome-ignore lint/correctness/useExhaustiveDependencies:  doens't work well for wei object
	const onSelectPercent = useCallback(
		(index: number) => {
			const percent = PERCENT_OPTIONS[index].replace('%', '')
			const margin = maxMargin.div(100).mul(percent)

			onSizeChange(floorNumber(margin).toString())
		},
		[maxMargin.toString(), onSizeChange]
	)

	const belowMinMargin = useMemo(() => margin !== '' && wei(margin).lt(MIN_MARGIN_AMOUNT), [margin])

	const invalid = margin !== '' && (maxMargin.lt(margin || 0) || belowMinMargin)

	return (
		<>
			<Container>
				<OrderSizingRow>
					<InputTitle>Margin</InputTitle>
					<InputHelpers>
						<SelectorButtons onSelect={onSelectPercent} options={PERCENT_OPTIONS} />
					</InputHelpers>
				</OrderSizingRow>

				<NumericInput
					invalid={invalid}
					dataTestId={'set-order-margin-susd-desktop'}
					value={isUserInput ? margin : initialValue}
					placeholder="0.00"
					right={belowMinMargin && <Body color="negative">Minimum $50</Body>}
					onChange={onChangeValue}
				/>
			</Container>
		</>
	)
})

const Container = styled.div`
	margin-top: 18px;
`

const OrderSizingRow = styled(FlexDivRow)`
	width: 100%;
	align-items: center;
	margin-bottom: 8px;
	cursor: default;
`

const InputHelpers = styled.div`
	display: flex;
`

export default EditOrderMarginInput
